import React, { CSSProperties, useEffect, useState } from 'react';

import { designTokens, styled } from '@project44/manifest-ds';
import { useIsMounted } from '@project44/ui-utils';

type Position = 'top' | 'bottom' | 'left' | 'right';

interface FloatInAnimationProps {
  visible: boolean;
  children: React.ReactNode;
  variant?: Position;
  timeout?: number;
}

const Container = styled('div');

const ONSCREEN_EDGE_OFFSET = '2.5rem';
const OFFSCREEN_EDGE_OFFSET = '-10rem';

export const FloatInAnimation = ({
  visible,
  children,
  variant = 'bottom',
  timeout = 500,
}: FloatInAnimationProps) => {
  const isMounted = useIsMounted();
  const [isVisible, setIsVisible] = useState<boolean>(visible);
  const [appear, setAppear] = useState<boolean>(false);

  const stylesSx = {
    position: 'fixed',
    width: 'max-content',
    maxWidth: '80vw',
    zIndex: designTokens.zIndexToast,
    transition: `${variant} ${timeout / 1000}s linear, opacity ${
      timeout / 1000
    }s linear`,
    ...(['top', 'bottom'].includes(variant)
      ? { left: '50%', transform: 'translate(-50%, 0)' }
      : {}),
    ...(['left', 'right'].includes(variant)
      ? { top: '50%', transform: 'translate(0, -50%)' }
      : {}),
  };

  const visibleSx = {
    ...stylesSx,
    [variant]: ONSCREEN_EDGE_OFFSET,
    opacity: '1',
  } as CSSProperties;

  const hiddenSx = {
    ...stylesSx,
    [variant]: OFFSCREEN_EDGE_OFFSET,
    opacity: '0',
  } as CSSProperties;

  useEffect(() => {
    if (visible) {
      setIsVisible(true);
      setTimeout(() => {
        if (isMounted()) {
          setAppear(true);
        }
      });
    } else {
      setAppear(false);
      // need timeout to delay setState to apply visibleSx after component was rendered with hiddenSx styles, otherwise the animation will not work
      setTimeout(() => {
        if (isMounted()) {
          setIsVisible(false);
        }
      }, timeout);
    }
  });

  if (!isVisible) return null;

  return (
    <Container style={appear ? visibleSx : hiddenSx}>{children}</Container>
  );
};
